/**
 * ManualInstallNotice — "you already have this installed by hand" callout for
 * the extension install pages.
 *
 * Both Baby Chops and Chopbook Tool moved to Chrome-Web-Store distribution
 * after a period of manual installs (Load Unpacked from a local build, or the
 * Drive ZIP the retired Baby Chops v1 page handed out). Anyone still on a
 * manual copy is in a worse state than they realise, which is why this says
 * more than "please reinstall":
 *
 *   - A Load-Unpacked extension has a DIFFERENT extension id from the Store
 *     build, so Chrome treats them as two unrelated extensions. Installing
 *     from the Store does not replace the manual copy — you end up with both
 *     enabled, and on a page where both inject, two panels.
 *   - The manual copy never auto-updates. It silently drifts behind while
 *     looking like it works, which is the failure this notice exists to stop.
 *
 * Shared by `BabyChopsV2Tool` and `ChopbookTool` rather than duplicated: the
 * copy is identical, and the whole point is that it says the same thing in
 * both places. It lives beside its two consumers rather than in
 * `@nu-assist/ui` because it is domain copy, not a reusable primitive.
 *
 * Colour: `bg-warning-muted` + `border-warning-strong` for the surface, and
 * `text-warning-ink` for the TEXT. That last one is deliberate — `tokens.css`
 * says amber text must use `--warning-ink` because the bright `--warning`
 * amber is illegible on the light theme. (`PreviewBanner` still uses
 * `text-warning`; do not copy it.)
 */
import { AlertTriangle } from "lucide-react";

export interface ManualInstallNoticeProps {
  /** Extension name as the reader knows it, e.g. "Baby Chops". */
  extensionName: string;
  /** Distinguishes the two instances in tests + DOM. */
  testId: string;
}

export function ManualInstallNotice({
  extensionName,
  testId,
}: ManualInstallNoticeProps) {
  return (
    <div
      data-testid={testId}
      className="mb-4 flex items-start gap-3 rounded-md border border-warning-strong bg-warning-muted p-3"
    >
      <AlertTriangle
        size={16}
        aria-hidden
        className="mt-0.5 shrink-0 text-warning-ink"
      />
      <p className="text-body text-warning-ink">
        <strong className="font-semibold">
          Already installed {extensionName} manually?
        </strong>{" "}
        Remove it first, then install from the Chrome Web Store below. A
        manually-loaded copy has a different extension ID, so the Store version
        installs <em>alongside</em> it rather than replacing it — you would end
        up running both, and the manual one never updates itself.
      </p>
    </div>
  );
}
